import { forwardRef, useEffect, useId, useMemo, useState } from 'react';

import { zodResolver } from '@hookform/resolvers/zod';
import * as Dialog from '@radix-ui/react-dialog';
import { Controller, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { z } from 'zod';

import { cn } from '@/styles/tailwind-utils';
import { Button } from '@components/ui/Button';
import { Input } from '@components/ui/Input';

import type {
  ClientContactMethod,
  ClientProfile,
  ClientReferralSource,
  ClientSex,
  EmergencyRelationship,
  UpdateClientProfileRequest,
} from '../types';
import { parseCnp } from '../utils/cnp';

import { DateOfBirthPicker } from './DateOfBirthPicker';


// ─── SelectField ─────────────────────────────────────────────────────────────

interface SelectFieldProps extends React.SelectHTMLAttributes<HTMLSelectElement> {
  label?: string;
  error?: string;
  children: React.ReactNode;
}

const SelectField = forwardRef<HTMLSelectElement, SelectFieldProps>(function SelectField(
  { label, error, id: idProp, children, className, ...props },
  ref,
) {
  const generatedId = useId();
  const id = idProp ?? generatedId;

  return (
    <div className="flex flex-col gap-1">
      {label && (
        <label htmlFor={id} className="text-sm font-medium text-foreground">
          {label}
        </label>
      )}
      <select
        ref={ref}
        id={id}
        className={cn(
          'h-10 w-full rounded-sm border border-input bg-surface px-3 py-2 text-sm text-foreground',
          'focus:outline-none focus:ring-2 focus:ring-brand focus:ring-offset-1',
          'disabled:cursor-not-allowed disabled:opacity-50',
          error && 'border-destructive focus:ring-destructive',
          className,
        )}
        {...props}
      >
        {children}
      </select>
      {error && (
        <p role="alert" className="text-sm text-destructive">
          {error}
        </p>
      )}
    </div>
  );
});

// ─── Constants ────────────────────────────────────────────────────────────────

const TABS = ['personal', 'contact', 'emergency', 'administrative'] as const;
type ProfileTab = (typeof TABS)[number];

// Country is fixed to Romania across the app — never shown, always sent as "RO".
const COUNTRY_RO = 'RO';

// The Bucharest municipality acts as its own county; selecting it reveals the
// sector field and auto-fills the city.
const BUCHAREST = 'București';

// All 41 Romanian counties (județe) plus the Bucharest municipality.
const COUNTIES: string[] = [
  'Alba', 'Arad', 'Argeș', 'Bacău', 'Bihor', 'Bistrița-Năsăud', 'Botoșani',
  'Brașov', 'Brăila', BUCHAREST, 'Buzău', 'Caraș-Severin', 'Călărași', 'Cluj',
  'Constanța', 'Covasna', 'Dâmbovița', 'Dolj', 'Galați', 'Giurgiu', 'Gorj',
  'Harghita', 'Hunedoara', 'Ialomița', 'Iași', 'Ilfov', 'Maramureș', 'Mehedinți',
  'Mureș', 'Neamț', 'Olt', 'Prahova', 'Satu Mare', 'Sălaj', 'Sibiu', 'Suceava',
  'Teleorman', 'Timiș', 'Tulcea', 'Vaslui', 'Vâlcea', 'Vrancea',
];

const SECTORS: string[] = [
  'Sector 1', 'Sector 2', 'Sector 3', 'Sector 4', 'Sector 5', 'Sector 6',
];

const SEX_OPTIONS: ClientSex[] = ['FEMALE', 'MALE', 'NOT_STATED'];
const CONTACT_METHODS: ClientContactMethod[] = ['EMAIL', 'SMS', 'PHONE'];
const RELATIONSHIPS: EmergencyRelationship[] = ['PARTNER', 'PARENT', 'SIBLING', 'FRIEND', 'OTHER'];
const REFERRAL_SOURCES: ClientReferralSource[] = ['WALK_IN', 'REFERRAL', 'ONLINE', 'OTHER'];

// Which form fields live under which tab — drives error navigation + tab dots.
const TAB_FIELDS: Record<ProfileTab, string[]> = {
  personal: ['firstName', 'lastName', 'preferredName', 'dateOfBirth', 'sex', 'cnp'],
  contact: ['phone', 'email', 'address', 'preferredContactMethod'],
  emergency: ['emergencyContact'],
  administrative: ['referralSource', 'referredBy', 'occupation', 'employer', 'internalNotes'],
};

const E164 = /^\+[1-9]\d{1,14}$/;
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

// ─── Component ────────────────────────────────────────────────────────────────

interface ClientProfileDrawerProps {
  isOpen: boolean;
  onClose: () => void;
  profile: ClientProfile;
  onSubmit: (data: UpdateClientProfileRequest) => void;
  isSubmitting: boolean;
  errorMessage: string | null;
}

export function ClientProfileDrawer({
  isOpen,
  onClose,
  profile,
  onSubmit,
  isSubmitting,
  errorMessage,
}: ClientProfileDrawerProps) {
  const { t, i18n } = useTranslation();
  const titleId = useId();

  const [activeTab, setActiveTab] = useState<ProfileTab>('personal');
  const [dirtyWarningVisible, setDirtyWarningVisible] = useState(false);
  const [dirtyWarningKey, setDirtyWarningKey] = useState(0);

  const today = useMemo(() => new Date().toISOString().slice(0, 10), []);

  const schema = useMemo(
    () =>
      z.object({
        // Personal
        firstName: z.string().min(1, t('clients.addClientModal.firstNameRequired')),
        lastName: z.string().min(1, t('clients.addClientModal.lastNameRequired')),
        preferredName: z.string(),
        dateOfBirth: z
          .string()
          .min(1, t('clients.addClientModal.dateOfBirthRequired'))
          .refine((v) => v <= today, t('clients.addClientModal.dateOfBirthFuture')),
        sex: z.string(),
        cnp: z
          .string()
          .refine((v) => v === '' || /^\d{13}$/.test(v), t('clients.addClientModal.cnpInvalid')),
        // Contact
        phone: z
          .string()
          .min(1, t('clients.addClientModal.phoneRequired'))
          .regex(E164, t('clients.addClientModal.phoneInvalid')),
        email: z
          .string()
          .min(1, t('clients.addClientModal.emailRequired'))
          .email(t('clients.addClientModal.emailInvalid')),
        address: z
          .object({
            county: z.string(),
            city: z.string(),
            sector: z.string(),
            street: z.string(),
            streetNumber: z.string(),
            building: z.string(),
            entrance: z.string(),
            apartment: z.string(),
            postalCode: z.string(),
          })
          .superRefine((addr, ctx) => {
            // Per the API contract, sector is required for Bucharest addresses.
            if (addr.county === BUCHAREST && !addr.sector) {
              ctx.addIssue({
                code: z.ZodIssueCode.custom,
                path: ['sector'],
                message: t('clients.profileDrawer.sectorRequired'),
              });
            }
          }),
        preferredContactMethod: z.string(),
        // Emergency contact — all optional, but validate format when present
        emergencyContact: z.object({
          fullName: z.string(),
          relationship: z.string(),
          phone: z
            .string()
            .refine((v) => v === '' || E164.test(v), t('clients.addClientModal.phoneInvalid')),
          email: z
            .string()
            .refine((v) => v === '' || EMAIL_RE.test(v), t('clients.addClientModal.emailInvalid')),
        }),
        // Administrative
        referralSource: z.string(),
        referredBy: z.string(),
        occupation: z.string(),
        employer: z.string(),
        internalNotes: z.string(),
      }),
    [t, today],
  );

  type FormValues = z.infer<typeof schema>;

  function buildDefaults(p: ClientProfile): FormValues {
    return {
      firstName: p.firstName ?? '',
      lastName: p.lastName ?? '',
      preferredName: p.preferredName ?? '',
      dateOfBirth: p.dateOfBirth ?? '',
      sex: p.sex ?? '',
      cnp: p.identifiers?.cnp ?? '',
      phone: p.phone ?? '',
      email: p.email ?? '',
      address: {
        county: p.address?.county ?? '',
        city: p.address?.city ?? '',
        sector: p.address?.sector ?? '',
        street: p.address?.street ?? '',
        streetNumber: p.address?.streetNumber ?? '',
        building: p.address?.building ?? '',
        entrance: p.address?.entrance ?? '',
        apartment: p.address?.apartment ?? '',
        postalCode: p.address?.postalCode ?? '',
      },
      preferredContactMethod: p.preferredContactMethod ?? '',
      emergencyContact: {
        fullName: p.emergencyContact?.fullName ?? '',
        relationship: p.emergencyContact?.relationship ?? '',
        phone: p.emergencyContact?.phone ?? '',
        email: p.emergencyContact?.email ?? '',
      },
      referralSource: p.referralSource ?? '',
      referredBy: p.referredBy ?? '',
      occupation: p.occupation ?? '',
      employer: p.employer ?? '',
      internalNotes: p.internalNotes ?? '',
    };
  }

  const {
    register,
    control,
    handleSubmit,
    reset,
    watch,
    setValue,
    getValues,
    formState: { errors, isDirty },
  } = useForm<FormValues>({
    resolver: zodResolver(schema),
    defaultValues: buildDefaults(profile),
  });

  useEffect(() => {
    if (isOpen) {
      reset(buildDefaults(profile));
      setActiveTab('personal');
      setDirtyWarningVisible(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, profile, reset]);

  // A complete CNP encodes the client's birth date and sex. When the user
  // changes the CNP away from the stored value, derive both and pre-fill the
  // matching fields. We never auto-fill while the CNP still equals the loaded
  // value, so the stored birth date/sex aren't overwritten on open.
  const loadedCnp = profile.identifiers?.cnp ?? '';
  const cnpValue = watch('cnp');
  const dateOfBirthValue = watch('dateOfBirth');
  const sexValue = watch('sex');

  useEffect(() => {
    if (cnpValue === loadedCnp) return;
    const parsed = parseCnp(cnpValue);
    if (!parsed) return;
    if (getValues('dateOfBirth') !== parsed.dateOfBirth) {
      setValue('dateOfBirth', parsed.dateOfBirth, { shouldValidate: true, shouldDirty: true });
    }
    if (getValues('sex') !== parsed.sex) {
      setValue('sex', parsed.sex, { shouldValidate: true, shouldDirty: true });
    }
  }, [cnpValue, loadedCnp, getValues, setValue]);

  // Non-blocking heads-up: the birth date / sex on the form diverge from what
  // the CNP encodes (e.g. the user manually overrode an auto-filled value).
  const parsedCnp = parseCnp(cnpValue);
  const cnpMismatch =
    parsedCnp !== null &&
    (parsedCnp.dateOfBirth !== dateOfBirthValue || parsedCnp.sex !== sexValue);

  // Selecting Bucharest as the county auto-fills the city and reveals the
  // sector field; switching away clears any previously chosen sector.
  const selectedCounty = watch('address.county');
  const isBucharest = selectedCounty === BUCHAREST;

  useEffect(() => {
    if (isBucharest) {
      if (getValues('address.city') !== BUCHAREST) {
        setValue('address.city', BUCHAREST, { shouldDirty: true });
      }
    } else if (getValues('address.sector')) {
      setValue('address.sector', '', { shouldDirty: true });
    }
  }, [isBucharest, getValues, setValue]);

  const emptyToNull = (v: string): string | null => {
    const trimmed = v.trim();
    return trimmed === '' ? null : trimmed;
  };

  function onFormSubmit(values: FormValues) {
    onSubmit({
      // Echo back the version we loaded so the server can enforce optimistic locking.
      version: profile.version ?? 0,
      firstName: values.firstName.trim(),
      lastName: values.lastName.trim(),
      preferredName: emptyToNull(values.preferredName),
      dateOfBirth: values.dateOfBirth,
      sex: (values.sex || null) as ClientSex | null,
      phone: values.phone.trim(),
      email: values.email.trim(),
      address: {
        county: emptyToNull(values.address.county),
        city: emptyToNull(values.address.city),
        // Sector only applies to Bucharest; null everywhere else.
        sector: isBucharest ? emptyToNull(values.address.sector) : null,
        street: emptyToNull(values.address.street),
        streetNumber: emptyToNull(values.address.streetNumber),
        building: emptyToNull(values.address.building),
        entrance: emptyToNull(values.address.entrance),
        apartment: emptyToNull(values.address.apartment),
        postalCode: emptyToNull(values.address.postalCode),
        // Country is fixed to Romania.
        country: COUNTRY_RO,
      },
      preferredContactMethod: (values.preferredContactMethod || null) as ClientContactMethod | null,
      emergencyContact: {
        fullName: emptyToNull(values.emergencyContact.fullName),
        relationship: (values.emergencyContact.relationship || null) as EmergencyRelationship | null,
        phone: emptyToNull(values.emergencyContact.phone),
        email: emptyToNull(values.emergencyContact.email),
      },
      identifiers: {
        cnp: emptyToNull(values.cnp),
        // Not edited here — preserve them so the full-replacement PUT doesn't clear them.
        nationalId: profile.identifiers?.nationalId ?? null,
        passport: profile.identifiers?.passport ?? null,
      },
      referralSource: (values.referralSource || null) as ClientReferralSource | null,
      referredBy: emptyToNull(values.referredBy),
      occupation: emptyToNull(values.occupation),
      employer: emptyToNull(values.employer),
      internalNotes: emptyToNull(values.internalNotes),
      // Not edited here — preserve it so the full-replacement PUT doesn't clear it.
      medicalAlert: profile.medicalAlert ?? null,
    });
  }

  // On validation failure, jump to the first tab that contains an error.
  function onInvalid() {
    const firstBadTab = TABS.find((tab) =>
      TAB_FIELDS[tab].some((f) => Boolean(errors[f as keyof FormValues])),
    );
    if (firstBadTab) setActiveTab(firstBadTab);
  }

  function tabHasError(tab: ProfileTab): boolean {
    return TAB_FIELDS[tab].some((f) => Boolean(errors[f as keyof FormValues]));
  }

  function handleInteractOutside(e: Event) {
    if (isDirty) {
      e.preventDefault();
      setDirtyWarningVisible(true);
      setDirtyWarningKey((k) => k + 1);
    }
  }

  const lastEdited = useMemo(() => {
    if (!profile.updatedAt) return null;
    const locale = i18n.language.startsWith('ro') ? 'ro' : 'en';
    return new Date(profile.updatedAt).toLocaleDateString(locale, {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
  }, [profile.updatedAt, i18n.language]);

  return (
    <Dialog.Root open={isOpen} onOpenChange={(open) => { if (!open) onClose(); }}>
      <Dialog.Portal>
        {/* Backdrop */}
        <Dialog.Overlay
          className={cn(
            'fixed inset-0 z-50 bg-black/40 backdrop-blur-[1px]',
            'data-[state=open]:animate-in data-[state=closed]:animate-out',
            'data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0',
          )}
        />

        {/* Drawer panel — slides in from the RIGHT */}
        <Dialog.Content
          aria-modal
          aria-labelledby={titleId}
          onPointerDownOutside={handleInteractOutside}
          onInteractOutside={handleInteractOutside}
          className={cn(
            'fixed right-0 top-0 z-50 flex h-full w-full flex-col bg-surface shadow-2xl',
            'sm:w-[48%] sm:min-w-[480px] sm:max-w-[680px]',
            'data-[state=open]:animate-in data-[state=closed]:animate-out',
            'data-[state=closed]:slide-out-to-right data-[state=open]:slide-in-from-right',
            'duration-300 ease-in-out',
          )}
        >
          {/* Header */}
          <div className="flex shrink-0 items-start justify-between border-b border-border px-6 py-4">
            <div className="min-w-0">
              <Dialog.Title id={titleId} className="text-base font-semibold text-foreground">
                {t('clients.profileDrawer.title')}
              </Dialog.Title>
              <p className="mt-0.5 truncate text-xs text-muted-foreground">
                {`${profile.firstName} ${profile.lastName}`}
              </p>
            </div>
            <Dialog.Close asChild>
              <button
                aria-label={t('layout.closeDialog')}
                className="flex h-8 w-8 shrink-0 items-center justify-center rounded-md text-muted-foreground hover:bg-muted hover:text-foreground focus:outline-none focus:ring-2 focus:ring-brand"
              >
                <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" aria-hidden="true">
                  <path d="M2 2l12 12M14 2L2 14" />
                </svg>
              </button>
            </Dialog.Close>
          </div>

          {/* Tabs */}
          <div className="shrink-0 border-b border-border px-4">
            <nav
              role="tablist"
              aria-label={t('clients.profileDrawer.tabsLabel')}
              className="-mb-px flex gap-1 overflow-x-auto"
            >
              {TABS.map((tab) => {
                const isActive = activeTab === tab;
                return (
                  <button
                    key={tab}
                    type="button"
                    role="tab"
                    aria-selected={isActive}
                    onClick={() => setActiveTab(tab)}
                    className={cn(
                      'flex items-center gap-1.5 whitespace-nowrap border-b-2 px-3 py-3 text-sm font-medium transition-colors',
                      isActive
                        ? 'border-primary text-primary'
                        : 'border-transparent text-muted-foreground hover:text-foreground',
                    )}
                  >
                    {t(`clients.profileDrawer.tabs.${tab}`)}
                    {tabHasError(tab) && (
                      <span className="h-1.5 w-1.5 rounded-full bg-destructive" aria-hidden="true" />
                    )}
                  </button>
                );
              })}
            </nav>
          </div>

          {/* Unsaved-changes warning — flashes when user tries to dismiss */}
          {dirtyWarningVisible && (
            <div
              key={dirtyWarningKey}
              role="status"
              className="shrink-0 border-b border-amber-200/80 bg-amber-50 px-6 py-2.5"
              style={{ animation: 'drawer-flash 0.4s ease 2' }}
            >
              <style>{`@keyframes drawer-flash{0%,100%{opacity:1}50%{opacity:0.1}}`}</style>
              <p className="text-sm text-amber-800">
                <span className="font-semibold">{t('clients.addClientDrawer.unsavedTitle')}</span>
                {' '}{t('clients.addClientDrawer.unsavedDesc')}
              </p>
            </div>
          )}

          {/* Scrollable body */}
          <div className="flex-1 overflow-y-auto px-6 py-5">
            {errorMessage && (
              <div role="alert" className="mb-5 rounded-md bg-destructive/10 p-3 text-sm text-destructive">
                {errorMessage}
              </div>
            )}

            <form
              id="client-profile-form"
              onSubmit={handleSubmit(onFormSubmit, onInvalid)}
              noValidate
            >
              {/* ── Personal details ── */}
              <div className={cn('space-y-4', activeTab !== 'personal' && 'hidden')}>
                <div className="grid grid-cols-2 gap-3">
                  <Input
                    label={t('clients.addClientModal.firstNameLabel')}
                    error={errors.firstName?.message}
                    {...register('firstName')}
                  />
                  <Input
                    label={t('clients.addClientModal.lastNameLabel')}
                    error={errors.lastName?.message}
                    {...register('lastName')}
                  />
                </div>

                <Input
                  label={t('clients.profileDrawer.preferredNameLabel')}
                  {...register('preferredName')}
                />

                <Input
                  label={t('clients.addClientModal.cnpLabel')}
                  inputMode="numeric"
                  placeholder={t('clients.addClientModal.cnpPlaceholder')}
                  error={errors.cnp?.message}
                  {...register('cnp')}
                />

                {cnpMismatch && (
                  <div
                    role="status"
                    className="flex items-start gap-2 rounded-sm border border-amber-200/80 bg-amber-50 px-3 py-2 text-sm text-amber-800"
                  >
                    <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5" className="mt-0.5 shrink-0" aria-hidden="true">
                      <path d="M8 1.5 15 14H1L8 1.5Z" strokeLinejoin="round" />
                      <path d="M8 6.5v3.5" strokeLinecap="round" />
                      <circle cx="8" cy="11.75" r="0.5" fill="currentColor" />
                    </svg>
                    <span>{t('clients.profileDrawer.cnpMismatchWarning')}</span>
                  </div>
                )}

                <div className="grid grid-cols-2 gap-3">
                  <Controller
                    control={control}
                    name="dateOfBirth"
                    render={({ field, fieldState }) => (
                      <DateOfBirthPicker
                        label={t('clients.addClientModal.dateOfBirthLabel')}
                        value={field.value}
                        onChange={field.onChange}
                        error={fieldState.error?.message}
                      />
                    )}
                  />
                  <SelectField
                    label={t('clients.profileDrawer.sexLabel')}
                    {...register('sex')}
                  >
                    <option value="">{t('clients.addClientModal.genderPlaceholder')}</option>
                    {SEX_OPTIONS.map((s) => (
                      <option key={s} value={s}>
                        {t(`clients.addClientModal.gender.${s}`)}
                      </option>
                    ))}
                  </SelectField>
                </div>
              </div>

              {/* ── Contact information ── */}
              <div className={cn('space-y-4', activeTab !== 'contact' && 'hidden')}>
                <div className="grid grid-cols-2 gap-3">
                  <Input
                    label={t('clients.addClientModal.phoneLabel')}
                    type="tel"
                    placeholder={t('clients.addClientModal.phonePlaceholder')}
                    error={errors.phone?.message}
                    {...register('phone')}
                  />
                  <Input
                    label={t('clients.addClientModal.emailLabel')}
                    type="email"
                    error={errors.email?.message}
                    {...register('email')}
                  />
                </div>

                <fieldset className="space-y-3">
                  <legend className="mb-1 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                    {t('clients.profileDrawer.addressLegend')}
                  </legend>

                  {/* County + City */}
                  <div className="grid grid-cols-2 gap-3">
                    <SelectField
                      label={t('clients.profileDrawer.countyLabel')}
                      {...register('address.county')}
                    >
                      <option value="">{t('clients.profileDrawer.countyPlaceholder')}</option>
                      {COUNTIES.map((c) => (
                        <option key={c} value={c}>
                          {c}
                        </option>
                      ))}
                    </SelectField>
                    <Input label={t('clients.profileDrawer.cityLabel')} {...register('address.city')} />
                  </div>

                  {/* Street */}
                  <Input label={t('clients.profileDrawer.streetLabel')} {...register('address.street')} />

                  {/* Number + Building + Entrance + Apartment */}
                  <div className="grid grid-cols-4 gap-3">
                    <Input label={t('clients.profileDrawer.streetNumberLabel')} {...register('address.streetNumber')} />
                    <Input label={t('clients.profileDrawer.buildingLabel')} {...register('address.building')} />
                    <Input label={t('clients.profileDrawer.entranceLabel')} {...register('address.entrance')} />
                    <Input label={t('clients.profileDrawer.apartmentLabel')} {...register('address.apartment')} />
                  </div>

                  {/* Postal code + Sector (Bucharest only) */}
                  <div className="grid grid-cols-2 gap-3">
                    <Input label={t('clients.profileDrawer.postalCodeLabel')} {...register('address.postalCode')} />
                    {isBucharest && (
                      <SelectField
                        label={t('clients.profileDrawer.sectorLabel')}
                        error={errors.address?.sector?.message}
                        {...register('address.sector')}
                      >
                        <option value="">{t('clients.profileDrawer.sectorPlaceholder')}</option>
                        {SECTORS.map((s) => (
                          <option key={s} value={s}>
                            {s}
                          </option>
                        ))}
                      </SelectField>
                    )}
                  </div>
                </fieldset>

                <SelectField
                  label={t('clients.profileDrawer.preferredContactMethodLabel')}
                  {...register('preferredContactMethod')}
                >
                  <option value="">{t('clients.profileDrawer.selectPlaceholder')}</option>
                  {CONTACT_METHODS.map((m) => (
                    <option key={m} value={m}>
                      {t(`clients.profileDrawer.contactMethod.${m}`)}
                    </option>
                  ))}
                </SelectField>
              </div>

              {/* ── Emergency contact ── */}
              <div className={cn('space-y-4', activeTab !== 'emergency' && 'hidden')}>
                <div className="grid grid-cols-2 gap-3">
                  <Input
                    label={t('clients.profileDrawer.emergencyNameLabel')}
                    {...register('emergencyContact.fullName')}
                  />
                  <SelectField
                    label={t('clients.profileDrawer.relationshipLabel')}
                    {...register('emergencyContact.relationship')}
                  >
                    <option value="">{t('clients.profileDrawer.selectPlaceholder')}</option>
                    {RELATIONSHIPS.map((r) => (
                      <option key={r} value={r}>
                        {t(`clients.profileDrawer.relationship.${r}`)}
                      </option>
                    ))}
                  </SelectField>
                </div>

                <div className="grid grid-cols-2 gap-3">
                  <Input
                    label={t('clients.addClientModal.phoneLabel')}
                    type="tel"
                    placeholder={t('clients.addClientModal.phonePlaceholder')}
                    error={errors.emergencyContact?.phone?.message}
                    {...register('emergencyContact.phone')}
                  />
                  <Input
                    label={t('clients.profileDrawer.emergencyEmailLabel')}
                    type="email"
                    placeholder={t('clients.profileDrawer.optionalPlaceholder')}
                    error={errors.emergencyContact?.email?.message}
                    {...register('emergencyContact.email')}
                  />
                </div>
              </div>

              {/* ── Administrative ── */}
              <div className={cn('space-y-4', activeTab !== 'administrative' && 'hidden')}>
                <div className="grid grid-cols-2 gap-3">
                  <SelectField
                    label={t('clients.profileDrawer.referralSourceLabel')}
                    {...register('referralSource')}
                  >
                    <option value="">{t('clients.profileDrawer.selectPlaceholder')}</option>
                    {REFERRAL_SOURCES.map((s) => (
                      <option key={s} value={s}>
                        {t(`clients.addClientModal.referralSource.${s}`)}
                      </option>
                    ))}
                  </SelectField>
                  <Input
                    label={t('clients.profileDrawer.referredByLabel')}
                    {...register('referredBy')}
                  />
                </div>

                <div className="grid grid-cols-2 gap-3">
                  <Input label={t('clients.profileDrawer.occupationLabel')} {...register('occupation')} />
                  <Input label={t('clients.profileDrawer.employerLabel')} {...register('employer')} />
                </div>

                <div className="flex flex-col gap-1">
                  <label htmlFor="profile-internal-notes" className="text-sm font-medium text-foreground">
                    {t('clients.profileDrawer.internalNotesLabel')}
                  </label>
                  <textarea
                    id="profile-internal-notes"
                    rows={4}
                    placeholder={t('clients.profileDrawer.internalNotesPlaceholder')}
                    className={cn(
                      'w-full rounded-sm border border-input bg-surface px-3 py-2 text-sm text-foreground',
                      'focus:outline-none focus:ring-2 focus:ring-brand focus:ring-offset-1',
                    )}
                    {...register('internalNotes')}
                  />
                </div>
              </div>
            </form>
          </div>

          {/* Footer */}
          <div className="flex shrink-0 items-center justify-between gap-3 border-t border-border px-6 py-4">
            <p className="text-xs text-muted-foreground">
              {lastEdited ? t('clients.profileDrawer.lastEdited', { date: lastEdited }) : ''}
            </p>
            <div className="flex items-center gap-3">
              <Button type="button" variant="secondary" onClick={onClose} disabled={isSubmitting}>
                {t('clients.profileDrawer.discard')}
              </Button>
              <Button type="submit" form="client-profile-form" isLoading={isSubmitting}>
                {t('clients.profileDrawer.save')}
              </Button>
            </div>
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}
